import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import { matchesPresetSearch } from "./filterBarUtils";

type Props = {
    /** Presets that did not fit in the tab bar, in stored order */
    overflow: IFilterPresetRuntime[];
    /** Every preset; searched when the user types so an inline tab can be found too */
    all: IFilterPresetRuntime[];
    activePresetId: string | null;
    /** When true a search box renders above the list */
    searchable: boolean;
    /** Bounding rect of the "More" tab the menu hangs from */
    anchor: DOMRect;
    translate: (id: string, values?: { [key: string]: string | number }) => string;
    onSelect: (preset: IFilterPresetRuntime) => void;
    onContextMenu: (preset: IFilterPresetRuntime, x: number, y: number) => void;
    onManage: () => void;
    onClose: () => void;
};

/**
 * Dropdown for the preset tabs that did not fit inline. With no query it lists the overflow only;
 * once the user types it searches every preset by name. Closes on outside click, Escape, scroll or resize.
 */
export const PresetOverflowMenu = (props: Props) => {
    const ref = useRef<HTMLDivElement>(null);
    const { onClose, translate, searchable } = props;
    const [query, setQuery] = useState("");

    useEffect(() => {
        const onMouseDown = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        };

        document.addEventListener("mousedown", onMouseDown);
        document.addEventListener("keydown", onKeyDown);
        window.addEventListener("scroll", onClose, true);
        window.addEventListener("resize", onClose);

        return () => {
            document.removeEventListener("mousedown", onMouseDown);
            document.removeEventListener("keydown", onKeyDown);
            window.removeEventListener("scroll", onClose, true);
            window.removeEventListener("resize", onClose);
        };
    }, [onClose]);

    const items = useMemo(() => {
        if (!searchable || !query.trim()) {
            return props.overflow;
        }

        return props.all.filter((p) => matchesPresetSearch(p.name, query));
    }, [searchable, query, props.overflow, props.all]);

    // Keep the menu on screen at narrow widths: 20rem max-width (see FilterBar.scss) plus a small gutter.
    const maxLeft = Math.max(8, window.innerWidth - 328);
    const style: React.CSSProperties = { top: props.anchor.bottom + 2, left: Math.min(props.anchor.left, maxLeft) };

    return createPortal(
        <div
            ref={ref}
            className={"FilterBar-context-menu FilterBar-overflow-menu"}
            style={style}
            role={"menu"}
            data-testid={"filterbar-more-menu"}>
            {searchable ? (
                <div className={"FilterBar-overflow-search"}>
                    <input
                        type={"search"}
                        className={"form-control form-control-sm"}
                        placeholder={translate("search_presets")}
                        aria-label={translate("search_presets")}
                        autoFocus={true}
                        value={query}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
                        onKeyDown={(e: React.KeyboardEvent) => {
                            if (e.key === "Enter" && items.length === 1) {
                                props.onSelect(items[0]);
                            }
                        }}
                    />
                </div>
            ) : null}

            <div className={"FilterBar-overflow-list"}>
                {items.length === 0 ? <div className={"FilterBar-overflow-empty"}>{translate("no_matching_presets")}</div> : null}

                {items.map((preset) => {
                    const isActive = preset.id === props.activePresetId;

                    return (
                        <button
                            key={preset.id}
                            type={"button"}
                            role={"menuitem"}
                            className={"FilterBar-context-item" + (isActive ? " active" : "")}
                            title={preset.source === "team" ? translate("team_preset") : undefined}
                            onClick={() => props.onSelect(preset)}
                            onContextMenu={(e: React.MouseEvent) => {
                                if (preset.editable) {
                                    e.preventDefault();
                                    props.onContextMenu(preset, e.clientX, e.clientY);
                                }
                            }}>
                            {preset.source === "team" ? <i className={"fas fa-users me-2"} /> : null}
                            {preset.isDefault ? <i className={"fas fa-star me-2 FilterBar-default-star"} /> : null}
                            <span>{preset.name}</span>
                        </button>
                    );
                })}
            </div>

            <div className={"FilterBar-overflow-footer"}>
                <button type={"button"} role={"menuitem"} className={"FilterBar-context-item"} onClick={props.onManage}>
                    <i className={"fas fa-cog me-2"} />
                    {translate("manage_presets")}
                </button>
            </div>
        </div>,
        document.body
    );
};
